import type { MenuItemProps } from '@mui/material';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import classNames from 'classnames/bind';
import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';

import styles from './DropdownMenu.module.scss';

const cx = classNames.bind(styles);

type DropdownMenuContextValue = {
  onClose: () => void;
  /** Queue an action to run once the menu has finished its exit transition. */
  queue: (action: () => void) => void;
};

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(null);

export type DropdownMenuProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Which edge the menu lines up with. Collapses the anchorOrigin pairs. */
  align?: 'start' | 'end';
  /**
   * Override the derived origins when a menu needs to open *beside* its anchor
   * rather than flush with it (asymmetric anchor/transform horizontals).
   */
  anchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' };
  transformOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' };
  width?: number;
  /** Stop clicks reaching a draggable or clickable ancestor. */
  stopPropagation?: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: MUI does not export TransitionProps
  TransitionProps?: any;
  className?: string;
  children?: React.ReactNode;
};

export type DropdownMenuItemProps = Omit<MenuItemProps, 'onSelect' | 'children'> & {
  children: React.ReactNode;
  icon?: React.ReactNode;
  onSelect?: (event: React.MouseEvent<HTMLLIElement>) => void;
  tone?: 'default' | 'danger';
  /** Declarative alternative to wrapping the item in `cond && (...)`. */
  hidden?: boolean;
  /** Leave the menu open after selection, e.g. for a multi-select filter. */
  keepOpen?: boolean;
  /**
   * Run `onSelect` only once the menu has finished exiting.
   *
   * Use for actions that take focus — opening an inline editor, a modal, an
   * emoji picker. MUI restores focus to the trigger as it closes, so running
   * immediately loses it. This replaces the `setTimeout(fn, 0)` workaround,
   * which raced the transition instead of waiting for it.
   */
  deferUntilClosed?: boolean;
};

/**
 * One menu row.
 *
 * Unknown props are forwarded to MUI's `MenuItem`: `MenuList` clones its
 * children to drive keyboard navigation, so swallowing them here would break
 * arrow-key focus.
 */
function DropdownMenuItem({
  children,
  icon,
  onSelect,
  tone = 'default',
  hidden = false,
  keepOpen = false,
  deferUntilClosed = false,
  className,
  ...rest
}: DropdownMenuItemProps) {
  const context = useContext(DropdownMenuContext);

  if (hidden) {
    return null;
  }

  return (
    <MenuItem
      className={cx('item', { itemDanger: tone === 'danger' }, className)}
      onClick={(event) => {
        if (deferUntilClosed && context) {
          context.queue(() => onSelect?.(event));
        } else {
          onSelect?.(event);
        }
        if (!keepOpen) {
          context?.onClose();
        }
      }}
      {...rest}
    >
      {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
      <ListItemText>{children}</ListItemText>
    </MenuItem>
  );
}

DropdownMenuItem.displayName = 'DropdownMenu.Item';

/** Hairline rule between groups of items. */
function DropdownMenuSeparator() {
  return <div className={cx('separator')} />;
}

DropdownMenuSeparator.displayName = 'DropdownMenu.Separator';

/**
 * Anchored menu.
 *
 * Replaces seven hand-rolled MUI `Menu` blocks that each repeated the same
 * `anchorOrigin`/`transformOrigin` pair and `ListItemIcon`/`ListItemText`
 * structure. Pair it with `useAnchorMenu`, spreading its `menuProps`:
 *
 * ```tsx
 * <DropdownMenu {...menu.menuProps}>
 *   <DropdownMenu.Item icon={<EditIcon />} deferUntilClosed onSelect={rename}>
 *     Rename
 *   </DropdownMenu.Item>
 *   <DropdownMenu.Separator />
 *   <DropdownMenu.Item tone='danger' icon={<DeleteIcon />} onSelect={remove}>
 *     Delete
 *   </DropdownMenu.Item>
 * </DropdownMenu>
 * ```
 */
function DropdownMenu({
  anchorEl,
  open,
  onClose,
  align = 'end',
  anchorOrigin,
  transformOrigin,
  width,
  stopPropagation = true,
  TransitionProps,
  className,
  children,
}: DropdownMenuProps) {
  const horizontal = align === 'end' ? 'right' : 'left';
  const pendingAction = useRef<(() => void) | null>(null);

  const handleExited = useCallback(() => {
    const action = pendingAction.current;
    pendingAction.current = null;
    action?.();
    TransitionProps?.onExited?.();
  }, [TransitionProps]);

  const context = useMemo<DropdownMenuContextValue>(
    () => ({
      onClose,
      queue: (action) => {
        pendingAction.current = action;
      },
    }),
    [onClose]
  );

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={anchorOrigin ?? { vertical: 'bottom', horizontal }}
      transformOrigin={transformOrigin ?? { vertical: 'top', horizontal }}
      onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
      slotProps={{
        paper: { className: cx('paper', className) },
      }}
      MenuListProps={{ className: cx('list') }}
      TransitionProps={{ ...TransitionProps, onExited: handleExited }}
      sx={width ? { '& .MuiPaper-root': { minWidth: width } } : undefined}
    >
      <DropdownMenuContext.Provider value={context}>{children}</DropdownMenuContext.Provider>
    </Menu>
  );
}

DropdownMenu.displayName = 'DropdownMenu';
DropdownMenu.Item = DropdownMenuItem;
DropdownMenu.Separator = DropdownMenuSeparator;

export default DropdownMenu;
